import {
  Briefcase,
  Guitar,
  Heart,
  MoreHorizontal,
  PartyPopper,
  Tent,
  Ticket,
  Users,
} from "lucide-react";
import { TimeInput } from "@/components/ui/TimeInput";
import { AutofillBadge } from "./AutofillBadge";
import { FormField } from "./FormField";
import { StepFooter } from "./StepFooter";
import type { StepProps } from "./step-props";
import { shouldClearEndDate, validateEventDetails } from "./validators";
import { FieldError } from "@/components/ui/FieldError";
import step from "./step.module.css";
import styles from "./EventDetailsStep.module.css";

interface EventDetailsStepProps extends StepProps {
  autofilledFields: Set<string>;
}

// Icon per event type for the tappable card grid (bigger targets, zero
// typing). A Company's own types have none, and a grid draws icons on
// every tile or on none: a "more" glyph on a type that is not Other reads
// as a menu.
const EVENT_TYPE_ICONS: Record<string, typeof Heart> = {
  wedding: Heart,
  corporate: Briefcase,
  festival: Tent,
  private_party: PartyPopper,
  ticketed: Ticket,
  community: Users,
  touring_band: Guitar,
  other: MoreHorizontal,
};

export function EventDetailsStep({
  profile,
  formData,
  activeFields,
  showValidation,
  autofilledFields,
  invalidFields,
  setFieldValidity,
  updateField,
  onBack,
  onContinue,
  continueLabel,
  onShowValidation,
}: EventDetailsStepProps): React.JSX.Element {
  const copy = profile.eventDetailsStep;
  const errors = validateEventDetails(formData);

  const eventTypeError = showValidation ? errors.eventType : undefined;
  const drawEventTypeIcons = copy.eventTypeOptions.every(
    (t) => EVENT_TYPE_ICONS[t.value],
  );

  const handleContinue = (): void => {
    // invalidFields still gates Continue for date inputs: DateInputNZ swallows
    // unparseable text (never writes it to formData), so the validator only
    // ever sees "blank" there — the parse signal lives in the input.
    const noInvalid = !invalidFields || invalidFields.size === 0;
    if (Object.keys(errors).length === 0 && noInvalid) {
      onContinue();
    } else {
      onShowValidation([
        ...new Set([...Object.keys(errors), ...(invalidFields ?? [])]),
      ]);
    }
  };

  return (
    <div className={step.step}>
      <div>
        <h1 className={step.heading}>{copy.title}</h1>
        <div className={styles.fields}>
          <div>
            <label className={styles.label}>
              {copy.fields.eventType.label}
              <AutofillBadge
                field="eventType"
                autofilledFields={autofilledFields}
              />
            </label>
            <div
              className={styles.eventTypes}
              role="radiogroup"
              aria-label={copy.fields.eventType.label}
              aria-invalid={eventTypeError ? true : undefined}
            >
              {copy.eventTypeOptions.map((t) => {
                const Icon = drawEventTypeIcons
                  ? EVENT_TYPE_ICONS[t.value]
                  : undefined;
                const selected = formData.eventType === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => updateField("eventType", t.value)}
                    className={`${styles.eventType} ${
                      selected
                        ? styles.eventTypeSelected
                        : eventTypeError
                          ? styles.eventTypeInvalid
                          : ""
                    }`}
                  >
                    {Icon && <Icon className={styles.eventTypeIcon} />}
                    {t.label}
                  </button>
                );
              })}
            </div>
            <FieldError message={eventTypeError} />
            {activeFields?.has("eventTypeOther") !== false &&
              formData.eventType === "other" && (
                <div className={styles.otherField}>
                  <FormField
                    label={copy.fields.eventTypeOther.label}
                    field="eventTypeOther"
                    formData={formData}
                    autofilledFields={autofilledFields}
                    required
                    showValidation={showValidation}
                    error={errors.eventTypeOther}
                    onChange={(v) => updateField("eventTypeOther", v)}
                  />
                </div>
              )}
          </div>

          <div className={styles.pair}>
            <div>
              <FormField
                label={copy.fields.eventDate.label}
                field="eventDate"
                formData={formData}
                autofilledFields={autofilledFields}
                type="date"
                required
                showValidation={showValidation}
                error={errors.eventDate}
                onChange={(v) => {
                  updateField("eventDate", v);
                  if (shouldClearEndDate(v, formData.eventEndDate)) {
                    updateField("eventEndDate", "");
                  }
                }}
                onValidityChange={
                  setFieldValidity
                    ? (valid) => setFieldValidity("eventDate", valid)
                    : undefined
                }
              />
            </div>
            <div>
              <FormField
                label={copy.fields.eventEndDate.label}
                field="eventEndDate"
                formData={formData}
                autofilledFields={autofilledFields}
                type="date"
                optional={copy.fields.eventEndDate.optional}
                showValidation={showValidation}
                error={errors.eventEndDate}
                min={formData.eventDate || undefined}
                onChange={(v) => updateField("eventEndDate", v)}
                onValidityChange={
                  setFieldValidity
                    ? (valid) => setFieldValidity("eventEndDate", valid)
                    : undefined
                }
              />
            </div>
          </div>

          <div className={styles.pair}>
            <div>
              <label className={styles.label}>
                {copy.fields.eventStartTime.label}{" "}
                {copy.fields.eventStartTime.optional && (
                  <span className={styles.optional}>(optional)</span>
                )}
                <AutofillBadge
                  field="eventStartTime"
                  autofilledFields={autofilledFields}
                />
              </label>
              <TimeInput
                value={formData.eventStartTime || ""}
                onChange={(val) => updateField("eventStartTime", val)}
                error={showValidation ? errors.eventStartTime : undefined}
                onValidityChange={
                  setFieldValidity
                    ? (valid) => setFieldValidity("eventStartTime", valid)
                    : undefined
                }
              />
            </div>
            <div>
              <label className={styles.label}>
                {copy.fields.eventEndTime.label}{" "}
                {copy.fields.eventEndTime.optional && (
                  <span className={styles.optional}>(optional)</span>
                )}
                <AutofillBadge
                  field="eventEndTime"
                  autofilledFields={autofilledFields}
                />
              </label>
              <TimeInput
                value={formData.eventEndTime || ""}
                onChange={(val) => updateField("eventEndTime", val)}
                error={showValidation ? errors.eventEndTime : undefined}
                onValidityChange={
                  setFieldValidity
                    ? (valid) => setFieldValidity("eventEndTime", valid)
                    : undefined
                }
              />
            </div>
          </div>

          <FormField
            label={copy.fields.eventName.label}
            field="eventName"
            formData={formData}
            autofilledFields={autofilledFields}
            required
            showValidation={showValidation}
            error={errors.eventName}
            onChange={(v) => updateField("eventName", v)}
          />

          <FormField
            label={copy.fields.organization.label}
            field="organization"
            formData={formData}
            autofilledFields={autofilledFields}
            optional={copy.fields.organization.optional}
            onChange={(v) => updateField("organization", v)}
          />
        </div>
      </div>

      <StepFooter
        onBack={onBack}
        onContinue={handleContinue}
        continueLabel={continueLabel}
      />
    </div>
  );
}
